import { ImageSquareIcon } from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { OverflowFadeText } from '@/components/layout/OverflowFadeText';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useDialogContentContainer } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import type { MediaFile } from '@/types/studio';

export interface ExistingMediaSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whatever `useMediaLibrary()` currently holds — not scoped by this file. */
  items: readonly MediaFile[];
  isLoading: boolean;
  /** `useMediaLibrary()`'s own `error` — a failed read, distinct from an empty one. */
  error?: unknown;
  onRetry: () => void;
  /** True when the workspace holds more media than `items` currently carries. */
  hasMore: boolean;
  onLoadMore: () => void;
  onSelect: (item: MediaFile) => void;
}

function MediaTile({ item }: { item: MediaFile }): JSX.Element {
  const [failed, setFailed] = useState(false);
  const thumb = item.thumbnail || item.url;

  useEffect(() => setFailed(false), [thumb]);

  if (failed || !thumb) {
    return (
      <span className="absolute inset-0 flex items-center justify-center bg-muted text-muted-foreground">
        <ImageSquareIcon className="size-6" aria-hidden="true" />
      </span>
    );
  }
  return (
    <img
      src={thumb}
      alt=""
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      className="absolute inset-0 size-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Workspace images could not be loaded.';
}

/**
 * "Use an image I already have" — an existing-media SOURCE for the Concept
 * Workbench (audit unit #9, Studio → Concept dead end).
 *
 * This does NOT reuse `PaidAdSourceMediaPickerDialog` directly: that picker
 * is bound to a paid-ad draft and a Meta-only edge function
 * (`/api/paid-ad/source-media-catalog`), neither of which a Concept has. It
 * DOES reuse that component's shape — a right `Sheet` over a searchable
 * `Command` thumbnail grid, the error/empty/truncation states, and the
 * open/close focus management — and is fed by the generic, already-shipped
 * `useMediaLibrary()` hook instead: the same workspace-scoped media source
 * `AttachPhotoRail` (Home composer) and `AssetsPage` already read from. Data
 * fetching stays with the caller (`ConceptDesignBody`) per this directory's
 * own rule — this file is presentational only.
 *
 * Search is CLIENT-side over whatever page `items` currently holds — unlike
 * the paid-ad picker, `useMediaLibrary()` has no server-side name search.
 * So a query that finds nothing while `hasMore` is true is not a real "no
 * matches": it may only mean the match hasn't been paged in yet. That case
 * gets an actionable "load more" empty state instead of the flat "no
 * matching images" a fully-loaded, fully-searched library earns.
 */
export function ExistingMediaSheet({
  open,
  onOpenChange,
  items,
  isLoading,
  error,
  onRetry,
  hasMore,
  onLoadMore,
  onSelect,
}: ExistingMediaSheetProps): JSX.Element {
  const parentDialogContainer = useDialogContentContainer();
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const imagesOnly = useMemo(
    () => items.filter((item) => item.type === 'image' && item.url),
    [items],
  );
  const trimmedQuery = query.trim();
  const filtered = useMemo(() => {
    if (!trimmedQuery) return imagesOnly;
    const needle = trimmedQuery.toLowerCase();
    return imagesOnly.filter((item) => item.name.toLowerCase().includes(needle));
  }, [imagesOnly, trimmedQuery]);

  const showEmpty = !isLoading && !error && filtered.length === 0;
  const showResults = filtered.length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        portalContainer={parentDialogContainer}
        className="flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:w-96 sm:max-w-none"
        onOpenAutoFocus={(event) => {
          const active = document.activeElement;
          returnFocusRef.current = active instanceof HTMLElement ? active : null;
          event.preventDefault();
          requestAnimationFrame(() => searchInputRef.current?.focus());
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          requestAnimationFrame(() => returnFocusRef.current?.focus());
        }}
      >
        <SheetHeader className="gap-1 border-b border-border/60 px-5 pb-4 pt-5 pr-12 sm:px-6 sm:pr-12">
          <SheetTitle>Use an existing image</SheetTitle>
          <SheetDescription>
            Choose an image from this workspace, including anything made in Studio.
          </SheetDescription>
        </SheetHeader>

        <Command
          shouldFilter={false}
          className="flex min-h-0 flex-1 flex-col rounded-none"
          label="Search workspace images"
        >
          <CommandInput
            ref={searchInputRef}
            value={query}
            onValueChange={setQuery}
            aria-label="Search workspace images"
            placeholder="Search workspace images"
          />
          {/*
           * `CommandList` hardcodes `max-h-[300px] overflow-y-auto` (src/components/ui/command.tsx).
           * `tailwind-merge` does not treat `max-h-[300px]` and `max-h-none` as
           * the same conflict group (verified: both survive `cn()` untouched),
           * so a bare `max-h-none` here is silently dropped by CSS source
           * order rather than by class-merge intent — measured live: tiles
           * stopped at ~300px inside a ~728px Sheet. `!max-h-none` forces the
           * override deterministically instead of depending on stylesheet order. */}
          <CommandList className="!max-h-none min-h-0 flex-1 p-2">
            {isLoading ? (
              <div
                className="grid grid-cols-2 gap-2 p-1 sm:grid-cols-3"
                aria-label="Loading workspace images"
                aria-busy="true"
              >
                {Array.from({ length: 6 }, (_, index) => (
                  <Skeleton key={index} className="aspect-square rounded-lg" />
                ))}
              </div>
            ) : null}

            {!isLoading && error ? (
              <EmptyState
                variant="error"
                className="min-h-64 border-0 bg-transparent"
                title="Couldn't load workspace images"
                description={errorMessage(error)}
                retry={onRetry}
              />
            ) : null}

            {showEmpty && hasMore ? (
              <EmptyState
                variant="compact"
                icon={ImageSquareIcon}
                className="min-h-64"
                title={trimmedQuery ? 'No matching images yet' : 'Nothing shown yet'}
                description={
                  trimmedQuery
                    ? 'This only searches the images loaded so far. Load more to keep looking.'
                    : 'Load more of this workspace to see its images.'
                }
                actions={[{ label: 'Load more images', onClick: onLoadMore }]}
              />
            ) : null}

            {showEmpty && !hasMore ? (
              <EmptyState
                variant="compact"
                icon={ImageSquareIcon}
                className="min-h-64"
                title={trimmedQuery ? 'No matching images' : 'Nothing in this workspace yet'}
                description={
                  trimmedQuery
                    ? 'Try another file name.'
                    : 'Generate or upload an image, then return here to use it.'
                }
              />
            ) : null}

            {showResults ? (
              <CommandGroup className="p-1 [&_[cmdk-group-items]]:grid [&_[cmdk-group-items]]:grid-cols-2 [&_[cmdk-group-items]]:gap-2 sm:[&_[cmdk-group-items]]:grid-cols-3">
                {filtered.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={item.name}
                    aria-label={`Use ${item.name}`}
                    onSelect={() => onSelect(item)}
                    className="group relative aspect-square min-h-0 items-end overflow-hidden rounded-lg border border-border/50 bg-card p-0 data-[selected=true]:bg-card data-[selected=true]:ring-2 data-[selected=true]:ring-ring"
                  >
                    <MediaTile item={item} />
                    <span className="relative z-10 mt-auto block w-full border-t border-border/50 bg-background/90 px-2.5 py-2 text-left backdrop-blur-sm">
                      <OverflowFadeText className="block text-caption font-medium text-foreground/85">
                        {item.name}
                      </OverflowFadeText>
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>

          {showResults && hasMore ? (
            <div
              className="flex items-center justify-between gap-3 border-t border-border/60 px-5 py-3 text-detail text-muted-foreground sm:px-6"
              role="status"
            >
              <span>
                {trimmedQuery
                  ? 'Search only covers the images loaded so far.'
                  : 'Showing the most recently added images.'}
              </span>
              <button
                type="button"
                onClick={onLoadMore}
                className="shrink-0 font-medium text-foreground underline-offset-2 hover:underline"
              >
                Load more
              </button>
            </div>
          ) : null}
        </Command>
      </SheetContent>
    </Sheet>
  );
}
